// ═══════════════════════════════════════════════════════════════
// MÓDULO DE REPORTES DE NÓMINA
// Archivo: components/Payroll/Reports/PayrollReportsModule.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  BarChart3, Users, Building2, MapPin, TrendingUp, TrendingDown,
  Calculator, Shield, PieChart, BookOpen, Package,
  AlertCircle, RefreshCw, SlidersHorizontal, X, ChevronDown,
  FileSpreadsheet, FileText, FileDown, Loader2, CalendarDays,
} from 'lucide-react';

import { useCompany } from '../../../contexts/CompanyContext';
import api            from '../../../api/config';
import useReportData  from './hooks/useReportData';
import ReportTable    from './components/ReportTable';
import PayslipView    from './components/PayslipView';

// ─────────────────────────────────────────────────────────────
// Catálogo de tipos de reporte
// ─────────────────────────────────────────────────────────────
const REPORTS = [
  { id: 'period-summary',     label: 'Resumen por Período',      icon: BarChart3,    color: 'text-blue-600',    bg: 'bg-blue-50'    },
  { id: 'employee-detail',    label: 'Detalle por Empleado',     icon: Users,        color: 'text-indigo-600',  bg: 'bg-indigo-50'  },
  { id: 'by-department',      label: 'Por Departamento',         icon: Building2,    color: 'text-violet-600',  bg: 'bg-violet-50'  },
  { id: 'by-branch',          label: 'Por Sucursal',             icon: MapPin,       color: 'text-cyan-600',    bg: 'bg-cyan-50'    },
  { id: 'income-concepts',    label: 'Ingresos por Concepto',    icon: TrendingUp,   color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'deduction-concepts', label: 'Deducciones por Concepto', icon: TrendingDown, color: 'text-rose-600',    bg: 'bg-rose-50'    },
  { id: 'isr',                label: 'ISR — Fiscal',             icon: Calculator,   color: 'text-amber-600',   bg: 'bg-amber-50'   },
  { id: 'social-security',    label: 'Seguridad Social',         icon: Shield,       color: 'text-blue-600',    bg: 'bg-blue-50'    },
  { id: 'cost-distribution',  label: 'Distribución de Costos',   icon: PieChart,     color: 'text-orange-600',  bg: 'bg-orange-50'  },
  { id: 'accounting-entry',   label: 'Asiento Contable',         icon: BookOpen,     color: 'text-slate-600',   bg: 'bg-slate-50'   },
  { id: 'provisions',          label: 'Provisiones',              icon: Package,      color: 'text-amber-700',   bg: 'bg-amber-50'   },
  { id: 'monthly-consolidated',label: 'Consolidado Mensual',      icon: CalendarDays, color: 'text-teal-600',    bg: 'bg-teal-50'    },
  { id: 'payslips',            label: 'Recibos de Pago',          icon: FileText,     color: 'text-blue-700',    bg: 'bg-blue-50'    },
];

// ─────────────────────────────────────────────────────────────
// Formato moneda
// ─────────────────────────────────────────────────────────────
const fmtMoney = (v) =>
  'RD$ ' + parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─────────────────────────────────────────────────────────────
// Módulo principal
// ─────────────────────────────────────────────────────────────
export default function PayrollReportsModule() {
  const { currentCompany } = useCompany();

  // ── Tipo de reporte activo ──
  const [activeReport, setActiveReport] = useState(REPORTS[0].id);

  // ── Filtros ──
  const [periodoDesde,  setPeriodoDesde]  = useState('');
  const [periodoHasta,  setPeriodoHasta]  = useState('');
  const [mes,           setMes]           = useState('');
  const [tipo,          setTipo]          = useState('');
  const [departmentId,  setDepartmentId]  = useState('');
  const [orgUnitId,     setOrgUnitId]     = useState('');
  const [employeeId,    setEmployeeId]    = useState('');
  const [showFilters,   setShowFilters]   = useState(false);

  // ── Datos auxiliares para filtros ──
  const [departments, setDepartments] = useState([]);
  const [orgUnits,    setOrgUnits]    = useState([]);
  const [employees,   setEmployees]   = useState([]);

  // ── Hook de datos ──
  const {
    data, summary, columns, metadata,
    loading, exporting, error, hasData,
    fetchReport, exportExcel, exportPDF, exportCSV,
  } = useReportData(activeReport);

  useEffect(() => {
    if (!currentCompany?.id) return;
    Promise.all([
      api.get('/departments',           { params: { companyId: currentCompany.id } }).catch(() => ({ data: [] })),
      api.get('/organizational-units',  { params: { companyId: currentCompany.id } }).catch(() => ({ data: [] })),
      api.get('/employees',             { params: { companyId: currentCompany.id } }).catch(() => ({ data: [] })),
    ]).then(([d, o, e]) => {
      setDepartments(d.data?.data || d.data || []);
      setOrgUnits(o.data?.data   || o.data   || []);
      setEmployees(e.data?.data  || e.data   || []);
    });
  }, [currentCompany]);

  if (!currentCompany) {
    return (
      <div className="p-8 text-center text-gray-400 text-sm">
        Selecciona una compañía para ver los reportes.
      </div>
    );
  }

  const activeDef = REPORTS.find(r => r.id === activeReport);
  const ActiveIcon = activeDef?.icon;

  const hasAdvancedFilter = departmentId || orgUnitId || employeeId;

  const isConsolidated = activeReport === 'monthly-consolidated';
  const isPayslips     = activeReport === 'payslips';

  const buildFilters = () => ({
    companyId:    currentCompany.id,
    mes:          isConsolidated ? (mes || undefined) : undefined,
    periodoDesde: !isConsolidated ? (periodoDesde || undefined) : undefined,
    periodoHasta: !isConsolidated ? (periodoHasta || undefined) : undefined,
    tipo:         tipo         || undefined,
    departmentId: departmentId || undefined,
    orgUnitId:    orgUnitId    || undefined,
    employeeId:   employeeId   || undefined,
  });

  const handleConsultar = () => fetchReport(buildFilters());
  const handleClear = () => {
    setPeriodoDesde(''); setPeriodoHasta(''); setMes(''); setTipo('');
    setDepartmentId(''); setOrgUnitId(''); setEmployeeId('');
  };

  // ── Cambiar tipo reinicia el estado ──
  const handleChangeReport = (id) => {
    setActiveReport(id);
  };

  return (
    <div className="space-y-4">

      {/* ── Fila 1: selector de reporte + botones export ──────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">

        {/* Selector de tipo */}
        <div className="flex items-center gap-2">
          {activeDef && (
            <div className={`w-8 h-8 ${activeDef.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <ActiveIcon className={`w-4 h-4 ${activeDef.color}`} />
            </div>
          )}
          <div className="relative">
            <select
              value={activeReport}
              onChange={e => handleChangeReport(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm font-semibold text-gray-800 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 cursor-pointer min-w-[210px]"
            >
              {REPORTS.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Exportar — solo si hay datos y no es recibos */}
        {hasData && !isPayslips && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400 mr-1">Exportar:</span>
            <ExportBtn label="Excel" icon={FileSpreadsheet} color="text-green-700 border-green-200 hover:bg-green-50"
              loading={exporting === 'excel'} disabled={!!exporting || !data.length}
              onClick={() => exportExcel(buildFilters())} />
            <ExportBtn label="PDF" icon={FileText} color="text-red-700 border-red-200 hover:bg-red-50"
              loading={exporting === 'pdf'} disabled={!!exporting || !data.length}
              onClick={() => exportPDF(buildFilters())} />
            <ExportBtn label="CSV" icon={FileDown} color="text-gray-600 border-gray-200 hover:bg-gray-50"
              loading={exporting === 'csv'} disabled={!!exporting || !data.length}
              onClick={() => exportCSV(buildFilters())} />
          </div>
        )}
      </div>

      {/* ── Fila 2: filtros rápidos + toggle avanzados ────────── */}
      {!isPayslips && (
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filtro de mes (consolidado) o desde/hasta (resto) */}
          {isConsolidated ? (
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500 whitespace-nowrap">Mes</label>
              <input type="month" value={mes} onChange={e => setMes(e.target.value)}
                className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500 whitespace-nowrap">Desde</label>
                <input type="month" value={periodoDesde} onChange={e => setPeriodoDesde(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500 whitespace-nowrap">Hasta</label>
                <input type="month" value={periodoHasta} onChange={e => setPeriodoHasta(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white" />
              </div>
            </>
          )}

          {/* Tipo — oculto para consolidado */}
          {!isConsolidated && (
            <select value={tipo} onChange={e => setTipo(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400">
              <option value="">Todos los tipos</option>
              <option value="mensual">Mensual</option>
              <option value="quincenal">Quincenal</option>
            </select>
          )}

          {/* Toggle filtros avanzados */}
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
              showFilters || hasAdvancedFilter
                ? 'border-blue-400 text-blue-600 bg-blue-50'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal size={14} />
            Filtros
            {hasAdvancedFilter && <span className="w-2 h-2 rounded-full bg-blue-500" />}
          </button>

          {/* Limpiar */}
          {(periodoDesde || periodoHasta || mes || tipo || hasAdvancedFilter) && (
            <button onClick={handleClear}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">
              <X size={13} /> Limpiar
            </button>
          )}

          {/* Consultar */}
          <button
            onClick={handleConsultar}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors ml-auto"
          >
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Consultando...</>
              : <><BarChart3 size={14} /> Consultar</>
            }
          </button>
        </div>
      )}

      {/* ── Filtros avanzados expandibles ─────────────────────── */}
      {!isPayslips && showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex-wrap">
          {orgUnits.length > 0 && (
            <select value={orgUnitId} onChange={e => setOrgUnitId(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400">
              <option value="">Todas las sucursales</option>
              {orgUnits.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          )}
          {departments.length > 0 && (
            <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400">
              <option value="">Todos los departamentos</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          )}
          {employees.length > 0 && (
            <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400">
              <option value="">Todos los empleados</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellido}</option>)}
            </select>
          )}
          {(orgUnitId || departmentId || employeeId) && (
            <button onClick={() => { setOrgUnitId(''); setDepartmentId(''); setEmployeeId(''); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* ── Recibos de pago ────────────────────────────────────── */}
      {isPayslips && <PayslipView companyId={currentCompany.id} />}

      {/* ── Secciones de reporte (ocultas para recibos) ────────── */}
      {!isPayslips && (
        <>
          {/* ── Error ──────────────────────────────────────────────── */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={15} className="flex-shrink-0" /> {error}
            </div>
          )}

          {/* ── Estado inicial ─────────────────────────────────────── */}
          {!hasData && !loading && !error && (
            <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              {activeDef && <ActiveIcon className="w-9 h-9 text-gray-300 mb-2" />}
              <p className="text-sm font-medium text-gray-500">
                {isConsolidated
                  ? <>Selecciona un <strong>mes</strong> y presiona <strong>Consultar</strong></>
                  : <>Selecciona un período y presiona <strong>Consultar</strong></>
                }
              </p>
              <p className="text-xs text-gray-400 mt-1">Solo se muestran nóminas cerradas</p>
            </div>
          )}

          {/* ── Cargando ───────────────────────────────────────────── */}
          {loading && (
            <div className="flex items-center justify-center py-14 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <RefreshCw size={20} className="text-blue-400 animate-spin mr-2" />
              <span className="text-sm text-gray-500">Generando reporte...</span>
            </div>
          )}

          {/* ── Resultado ──────────────────────────────────────────── */}
          {hasData && !loading && (
            <>
              {/* Barra de stats */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="hidden sm:flex items-center gap-3">
                  {metadata?.totalRows != null && (
                    <span className="text-xs text-gray-400">
                      <span className="font-semibold text-blue-600">{metadata.totalRows.toLocaleString('es-DO')}</span> registros
                    </span>
                  )}
                  {summary?.total_neto != null && (
                    <>
                      <span className="text-gray-200">|</span>
                      <span className="text-xs text-gray-400">
                        Neto total: <span className="font-semibold text-emerald-600">{fmtMoney(summary.total_neto)}</span>
                      </span>
                    </>
                  )}
                  {summary?.costo_total != null && (
                    <>
                      <span className="text-gray-200">|</span>
                      <span className="text-xs text-gray-400">
                        Costo total: <span className="font-semibold text-orange-600">{fmtMoney(summary.costo_total)}</span>
                      </span>
                    </>
                  )}
                  {summary?.total_bruto != null && !summary?.total_neto && (
                    <>
                      <span className="text-gray-200">|</span>
                      <span className="text-xs text-gray-400">
                        Bruto total: <span className="font-semibold text-indigo-600">{fmtMoney(summary.total_bruto)}</span>
                      </span>
                    </>
                  )}
                </div>
                {metadata?.generatedAt && (
                  <span className="text-xs text-gray-300 ml-auto">
                    {new Date(metadata.generatedAt).toLocaleString('es-DO')}
                  </span>
                )}
              </div>

              <ReportTable columns={columns} data={data} summary={summary} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Botón de exportación pequeño
// ─────────────────────────────────────────────────────────────
function ExportBtn({ label, icon: Icon, color, loading, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-colors
        ${disabled ? 'opacity-50 cursor-not-allowed border-gray-200 text-gray-400' : color}`}
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
      {loading ? '...' : label}
    </button>
  );
}
